import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Divider,
  IconButton,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tooltip,
  Alert,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  Category as TypeIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as DateIcon,
  Send as InviteIcon,
  CheckCircle as ActiveIcon,
  Schedule as PendingIcon,
  Link as LinkIcon,
  Smartphone as HomeScreenIcon,
} from '@mui/icons-material';
import HomeScreenButtonsTab from './HomeScreenButtonsTab';

const PersonDetailsPanel = ({
  person,
  personType,
  location,
  onClose,
  onEdit,
  onDelete,
  onInvite,
  canEdit,
  canDelete,
  canInvite
}) => {
  const [activeTab, setActiveTab] = useState(0);

  if (!person) return null;

  const getInviteStatusInfo = () => {
    const status = person.inviteStatus || 'none';
    switch (status) {
      case 'pending':
        return {
          color: 'warning',
          icon: <PendingIcon />,
          label: 'Invite Pending',
          description: person.inviteExpires
            ? `Expires: ${new Date(person.inviteExpires).toLocaleDateString()}`
            : 'Waiting for acceptance'
        };
      case 'accepted':
        return {
          color: 'success',
          icon: <ActiveIcon />,
          label: 'Active User',
          description: 'Linked to user account'
        };
      case 'expired':
        return {
          color: 'error',
          icon: <PendingIcon />,
          label: 'Invite Expired',
          description: 'Invite link has expired'
        };
      default:
        return {
          color: 'default',
          icon: <PersonIcon />,
          label: 'Not Invited',
          description: 'No user account linked'
        };
    }
  };

  const inviteStatus = getInviteStatusInfo();

  // Get custom field icon
  const getFieldIcon = (fieldType) => {
    switch (fieldType) {
      case 'email':
        return <EmailIcon fontSize="small" />;
      case 'phone':
        return <PhoneIcon fontSize="small" />;
      case 'date':
        return <DateIcon fontSize="small" />;
      default:
        return null;
    }
  };

  // Render custom field value
  const renderFieldValue = (field, value) => {
    if (value === undefined || value === null || value === '') {
      return <Typography variant="body2" color="text.secondary">-</Typography>;
    }

    if (field.type === 'boolean') {
      return (
        <Chip
          size="small"
          label={value ? 'Yes' : 'No'}
          color={value ? 'success' : 'default'}
          variant="outlined"
        />
      );
    }

    if (field.type === 'date' && value) {
      return (
        <Typography variant="body2">
          {new Date(value).toLocaleDateString()}
        </Typography>
      );
    }

    return <Typography variant="body2">{String(value)}</Typography>;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderLeft: 1,
        borderColor: 'divider'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider'
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" noWrap>
            {person.friendlyName}
          </Typography>
          {personType && (
            <Chip
              icon={<TypeIcon sx={{ fontSize: 16 }} />}
              label={personType.friendlyName}
              size="small"
              sx={{ mt: 0.5 }}
            />
          )}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          variant="fullWidth"
          sx={{ minHeight: 40 }}
        >
          <Tab label="Overview" sx={{ minHeight: 40, py: 0.5, fontSize: '0.8rem' }} />
          <Tab
            label="Home Screen"
            icon={<HomeScreenIcon sx={{ fontSize: 16 }} />}
            iconPosition="start"
            sx={{ minHeight: 40, py: 0.5, fontSize: '0.8rem' }}
          />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {activeTab === 0 && (
          <>
            {/* User Status */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                User Status
              </Typography>
              <Alert
                severity={inviteStatus.color === 'default' ? 'info' : inviteStatus.color}
                icon={inviteStatus.icon}
                sx={{ mb: 1 }}
              >
                <Typography variant="body2" fontWeight="medium">
                  {inviteStatus.label}
                </Typography>
                <Typography variant="caption">
                  {inviteStatus.description}
                </Typography>
              </Alert>

              {canInvite && person.inviteStatus !== 'accepted' && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<InviteIcon />}
                  onClick={onInvite}
                  fullWidth
                >
                  {person.inviteStatus === 'pending' ? 'Resend Invite' : 'Invite to Portal'}
                </Button>
              )}

              {person.userId && (
                <Button
                  variant="text"
                  size="small"
                  startIcon={<LinkIcon />}
                  fullWidth
                  sx={{ mt: 1 }}
                >
                  View Linked User
                </Button>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Basic Info */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Basic Information
              </Typography>
              <List dense disablePadding>
                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <LocationIcon fontSize="small" color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Location"
                    secondary={location?.friendlyName || 'Not assigned'}
                  />
                </ListItem>
                {person.person_UUID && (
                  <ListItem disableGutters>
                    <ListItemText
                      primary="UUID"
                      secondary={
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {person.person_UUID}
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
                {person.email && (
                  <ListItem disableGutters>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <EmailIcon fontSize="small" color="action" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Email"
                      secondary={person.email}
                    />
                  </ListItem>
                )}
              </List>
            </Box>

            {/* Custom Fields */}
            {personType?.dataModel && personType.dataModel.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {personType.friendlyName} Details
                  </Typography>
                  <List dense disablePadding>
                    {personType.dataModel.map((field) => (
                      <ListItem key={field.key} disableGutters>
                        {getFieldIcon(field.type) && (
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            {getFieldIcon(field.type)}
                          </ListItemIcon>
                        )}
                        <ListItemText
                          primary={field.label}
                          secondary={renderFieldValue(field, person.data?.[field.key])}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </>
            )}

            {/* Timestamps */}
            <Divider sx={{ my: 2 }} />
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Timestamps
              </Typography>
              <List dense disablePadding>
                <ListItem disableGutters>
                  <ListItemText
                    primary="Created"
                    secondary={
                      person.created_at
                        ? new Date(person.created_at).toLocaleString()
                        : '-'
                    }
                  />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText
                    primary="Updated"
                    secondary={
                      person.updated_at
                        ? new Date(person.updated_at).toLocaleString()
                        : '-'
                    }
                  />
                </ListItem>
              </List>
            </Box>
          </>
        )}

        {activeTab === 1 && (
          <HomeScreenButtonsTab person={person} />
        )}
      </Box>

      {/* Actions */}
      <Box
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          gap: 1
        }}
      >
        {canEdit && (
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={onEdit}
            fullWidth
          >
            Edit
          </Button>
        )}
        {canDelete && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={onDelete}
            fullWidth
          >
            Delete
          </Button>
        )}
      </Box>
    </Paper>
  );
};

export default PersonDetailsPanel;
